import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChildFn,
  CanActivateFn,
  CanDeactivateFn,
  CanMatchFn,
  GuardResult,
  MaybeAsync,
  // RedirectCommand,
  Route,
  Router,
  // Router,
  RouterStateSnapshot,
  UrlSegment,
  type UrlTree,
} from '@angular/router';
import { AccessControl } from '@config/access-control';
import type { StringUnion } from '@shared/types/common';
import { noop } from 'rxjs';
import { AuthService } from './auth.service';

/**
 * AccessGuardCanMatchFn
 * This function is used as a guard for checking if the current user has access to the requested route.
 * It checks if the user has access to the route path by calling the userHasAccessToRoute() function from the AuthService.
 * The result of this function will determine if the user is allowed to navigate to the route or not.
 *
 *
 * @param route
 * @param segments
 * @constructor
 *
 * @author Carlos Duardo <charlieandroid55@gmail.com>`
 */
export const AccessGuardCanMatchFn: CanMatchFn = (
  route: Route,
  segments: UrlSegment[],
): MaybeAsync<GuardResult> => {
  const authService = inject(AuthService);
  return authService.userHasAccessToRoute(route.path);
};

/**
 * @param route
 * @param state
 * @constructor
 *
 * @author Carlos Duardo <charlieandroid55@gmail.com>
 */
export const AccessGuardCanActivate: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): MaybeAsync<GuardResult> => {
  const authService = inject(AuthService);
  const isFullyAuthenticated = authService.isFullyAuthenticated();

  if (!isFullyAuthenticated) {
    authService.logout({ clearServerSession: true });
    // const router: Router = inject(Router);
    // const urlTree: UrlTree = router.parseUrl(LOGIN_ROUTE);

    // return new RedirectCommand(urlTree, { skipLocationChange: true });
  }

  return isFullyAuthenticated;
};

/**
 *
 * @param childRoute
 * @param state
 * @constructor
 *
 * @author Carlos Duardo <charlieandroid55@gmail.com>
 *
 */
export const AccessGuardCanActivateChildFn: CanActivateChildFn = (
  childRoute: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): MaybeAsync<GuardResult> => {
  const authService = inject(AuthService);
  return authService.userHasAccessToRoute(childRoute.routeConfig?.path);
};

export interface CanDeactivateFnProps {
  currentRoute: ActivatedRouteSnapshot;
  currentState: RouterStateSnapshot;
  nextState: RouterStateSnapshot;
}

export type CanDeactivateCallbackFn = (
  props: CanDeactivateFnProps,
) => MaybeAsync<boolean>;

export interface CanDeactivateComponent {
  canDeactivate: CanDeactivateCallbackFn;
}

/**
 * This function is used to determine if a component can be deactivated,
 * this component must implements CanDeactivateComponent interface
 *
 * @param component
 * @param currentRoute
 * @param currentState
 * @param nextState
 * @returns
 */
export const AccessGuardCanDeactivateFn: CanDeactivateFn<
  CanDeactivateComponent
> = (
  component: CanDeactivateComponent,
  currentRoute: ActivatedRouteSnapshot,
  currentState: RouterStateSnapshot,
  nextState: RouterStateSnapshot,
): MaybeAsync<GuardResult> => {
  const props = {
    currentRoute,
    currentState,
    nextState,
  };
  return component.canDeactivate(props);
};

/**
 * Creates a route guard function that checks if the current user has the required role(s) to access a route.
 * If the user is not authorized, it optionally redirects or logs out the user based on the `redirectTo` parameter.
 *
 * @param role - A single role or an array of roles required to access the route.
 * @param redirectTo - Determines the action to take if access is denied:
 *   - `'default_url'`: Redirects to the default access denied route defined in the access control config
 *   - `'none'`: Does not perform any redirection or logout.
 *   - `'logout'`: Logs out the user and clears the server session.
 *   - Any other string: Redirects to the specified URL.
 *
 *  Defaults to `'default_url'`.
 *
 * @example
 * {
 *   path: 'admin',
 *   canActivate: [GuardCanAccessByRoleFn('ROLE_ADMIN')]
 * }
 * {
 *   path: 'user/edit/:id',
 *   canActivate: [GuardCanAccessByRoleFn(['ROLE_ADMIN', 'ROLE_SUPER'], 'logout')]
 * }
 *  * {
 *   path: 'user/profile',
 *   canActivate: [GuardCanAccessByRoleFn(['ROLE_ADMIN', 'ROLE_SUPER'], 'user/list')]
 * }
 *
 * @returns A function suitable for use as a route guard, returning `true` if access is allowed, otherwise `false`.
 */
export const GuardCanAccessByRoleFn = (
  role: string | string[],
  redirectTo: StringUnion<'default_url' | 'none' | 'logout'> = 'default_url',
) => {
  return () => {
    const authService = inject(AuthService);
    const canActivate = authService.isAuthorized(role);
    const router = inject(Router);

    if (!canActivate && 'none' !== redirectTo) {
      if ('logout' === redirectTo) {
        authService.logout({ clearServerSession: true });
      } else {
        const urlTree: UrlTree = router.parseUrl(
          'default_url' === redirectTo
            ? AccessControl.REDIRECT_ON_ACCESS_DENIED_ROUTE.url
            : redirectTo,
        );

        router.navigateByUrl(urlTree).catch(noop);
      }
    }

    return canActivate;
  };
};
